import { Fill, Style, Text } from 'ol/style.js';
import { asString } from 'ol/color';
import Overlay from 'ol/Overlay';
import h from 'hyperscript';
import Polygon from 'ol/geom/Polygon';
import { getArea, getLength } from 'ol/sphere';
import LineString from 'ol/geom/LineString';
import crosshair from '@disy/cadenza-icons/crosshair.svg';
import Icon from 'ol/style/Icon';
import ImageState from 'ol/ImageState';

import type { CoordinateSketchStyle, ImageSketchProperties, LineStringSketchStyle, MeasureSketchProperties, PointSketchStyle, PolygonSketchStyle, SketchStyleProperties, TextSketchStyle } from 'cadenza/workbook/sketch/sketch-layer/sketch-types';
import { getSketchItemDto, SketchFeatureProperty, SketchItemType } from 'cadenza/workbook/sketch/sketch-layer/sketch-types';
import { fonts, toCssFontSize } from 'cadenza/utils/fonts';
import { createStyle, DEFAULT_OL_Z_INDEX } from 'cadenza/map/style/create-style';
import { AnchorType, LineDecoration, PointSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { createSketchFeatureStrategy, createSketchStrategy, createSketchTypeStrategy } from 'cadenza/workbook/sketch/sketch-layer/sketch-strategy';
import { degreesToRadians } from 'cadenza/utils/math';
import type { RgbaColor } from 'cadenza/utils/colors/colors';
import { applyOpacityFactorToColor, getColorWithOpacity, toRgbaColor } from 'cadenza/utils/colors/colors';
import { uniqueId } from 'cadenza/utils/unique-id';
import { formatArea, formatLength } from 'cadenza/utils/format/format';
import { getMediaItemDownloadUrl } from 'cadenza/api-client/repositories-media-api';
import { assert, assertType } from 'cadenza/utils/custom-error';
import { setupCoordinatesOverlay } from 'cadenza/workbook/sketch/interactions/capture-coordinates-interaction';
import { deserializeGeometry } from 'cadenza/workbook/sketch/sketch-layer/sketch-format';
import { parseSvg } from 'cadenza/utils/svg';
import type { Map } from 'cadenza/map/map';
import { getImageObjectRenderer } from 'cadenza/map/geom/image-object-renderer';
import { on } from 'cadenza/utils/event-util';

import type { StyleLike } from 'ol/style/Style';
import type { Geometry } from 'ol/geom';
import type Feature from 'ol/Feature';
import type Projection from 'ol/proj/Projection';
import type { Coordinate } from 'ol/coordinate';

export type RenderingStrategy = (feature: Feature<Geometry>) => Promise<void | Style>;

export function getRenderingStrategy (map: Map) {
  return createSketchFeatureStrategy<Promise<Style | void>>({
    [SketchItemType.POINT]: feature => geometryRendering(feature),
    [SketchItemType.OVAL]: feature => geometryRendering(feature),
    [SketchItemType.RECTANGLE]: feature => geometryRendering(feature),
    [SketchItemType.POLYGON]: feature => geometryRendering(feature),
    [SketchItemType.LINE_STRING]: feature => geometryRendering(feature),
    [SketchItemType.DISTANCE_MEASUREMENT]: feature => measureRendering(feature, map),
    [SketchItemType.AREA_MEASUREMENT]: feature => measureRendering(feature, map),
    [SketchItemType.COORDINATE]: feature => coordinatesRendering(feature, map),
    [SketchItemType.TEXT]: feature => geometryRendering(feature),
    [SketchItemType.IMAGE]: feature => imageGeometryRendering(feature),
    [SketchItemType.SYMBOL]: feature => imageGeometryRendering(feature)
  });
}

async function geometryRendering (feature: Feature) {
  const style = await itemToOpenLayersStyle(getSketchItemDto(feature));
  feature.setStyle(style);
  return style;
}

async function imageGeometryRendering (feature: Feature) {
  const sketchItemDto = getSketchItemDto(feature);
  const style = await imageStyle((sketchItemDto.properties as ImageSketchProperties)); // For images we're sure it's defined
  const geometryFromSketchItemDto = deserializeGeometry(sketchItemDto);
  feature.setStyle(style);
  feature.setGeometry(geometryFromSketchItemDto);
  return style;
}

const itemToOpenLayersStyle = createSketchStrategy<Promise<Style>>({
  [SketchItemType.POINT]: item => Promise.resolve(pointStyleToOlStyle(item.styles as PointSketchStyle, item.zIndex)),
  [SketchItemType.OVAL]: item => Promise.resolve(polygonStyleToOlStyle(item.styles as PolygonSketchStyle, item.zIndex, GeometryType.OVAL)),
  [SketchItemType.RECTANGLE]: item => Promise.resolve(polygonStyleToOlStyle(item.styles as PolygonSketchStyle, item.zIndex, GeometryType.RECTANGLE)),
  [SketchItemType.POLYGON]: item => Promise.resolve(polygonStyleToOlStyle(item.styles as PolygonSketchStyle, item.zIndex, GeometryType.POLYGON)),
  [SketchItemType.LINE_STRING]: item => Promise.resolve(lineModelStyleToOlStyle(item.styles as LineStringSketchStyle, item.zIndex)),
  [SketchItemType.DISTANCE_MEASUREMENT]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'DistanceMeasurement', GeometryType.LINE_STRING)),
  [SketchItemType.AREA_MEASUREMENT]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'AreaMeasurement', GeometryType.POLYGON)),
  [SketchItemType.COORDINATE]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'Coordinate', GeometryType.POINT)),
  [SketchItemType.TEXT]: item => Promise.resolve(textStyle(item.styles as TextSketchStyle, item.zIndex)),
  [SketchItemType.IMAGE]: item => imageStyle((item.properties as ImageSketchProperties)),  // For images we're sure it's defined
  [SketchItemType.SYMBOL]: item => imageStyle((item.properties as ImageSketchProperties))  // For symbols we're sure it's defined
});

function pointStyleToOlStyle (style: PointSketchStyle, zIndex: number) {
  const styleConfiguration = {
    [StyleProperty.POINT_SIGNATURE_TYPE]: style[StyleProperty.POINT_SIGNATURE_TYPE],
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.SYMBOL_SIZE]: style[StyleProperty.SYMBOL_SIZE],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.BORDER_COLOR]: style[StyleProperty.BORDER_COLOR],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType: GeometryType.POINT });
}

function coordinateOrMeasureStyleToOlStyle (zIndex: number, sketchItemType: SketchItemType, geometryType: GeometryType) {
  const styleConfiguration = {
    ...getDefaultStyleStrategy()(sketchItemType),
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType });
}

function polygonStyleToOlStyle (style: PolygonSketchStyle, zIndex: number, geometryType: GeometryType) {
  const styleConfiguration = {
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.BORDER_COLOR]: style[StyleProperty.BORDER_COLOR],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType });
}

function lineModelStyleToOlStyle (style: LineStringSketchStyle, zIndex: number) {
  const styleConfiguration = {
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.LINE_START_DECORATION]: style[StyleProperty.LINE_START_DECORATION],
    [StyleProperty.LINE_END_DECORATION]: style[StyleProperty.LINE_END_DECORATION],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };

  return createStyle(styleConfiguration, { geometryType: GeometryType.LINE });
}

async function measureRendering (feature: Feature, map: Map) {
  const style = await itemToOpenLayersStyle(getSketchItemDto(feature));
  feature.setStyle(style);
  setupMeasureOverlay(feature, map);
  return Promise.resolve();
}

async function coordinatesRendering (feature: Feature, map: Map) {
  const style = coordinateOrMeasureStyleToOlStyle(getSketchItemDto(feature).zIndex, 'Coordinate', GeometryType.POINT);
  feature.setStyle(style);
  setupCoordinatesOverlay(feature, map);
  return Promise.resolve();
}

export function textStyle (style: TextSketchStyle, zIndex?: number) {
  const [ textBaseline, textAlign ] = AnchorType[style[StyleProperty.ANCHOR]];
  let fillColor = style[StyleProperty.BACKGROUND_VISIBLE] ? style[StyleProperty.BACKGROUND_COLOR] : asString(getColorWithOpacity(style[StyleProperty.BACKGROUND_COLOR], 0));
  // we take a negative value of the angle to match Cadenza Desktop behavior
  const rotation = style[StyleProperty.ROTATION] ? -degreesToRadians(style[StyleProperty.ROTATION]) : undefined;
  return new Style({
    text: new Text({
      backgroundFill: new Fill({ color: fillColor }),
      font: getFontStyle(style),
      text: style.text ?? '',
      fill: new Fill({ color: style[StyleProperty.FOREGROUND_COLOR] }),
      textBaseline,
      textAlign,
      placement: 'point',
      rotation,
      padding: [ 2, 0, 2, 0 ]
    }),
    zIndex: zIndex ?? DEFAULT_OL_Z_INDEX
  });
}

function getFontStyle (style: TextSketchStyle) {
  const fontStyle = [];
  if (style.bold) {
    fontStyle.push('bold');
  }
  if (style.italic) {
    fontStyle.push('italic');
  }
  fontStyle.push(toCssFontSize(style.fontSize));
  fontStyle.push(style.fontFamily);
  return fontStyle.join(' ');
}

export function getTextContentFromFeatureStyle (style?: StyleLike) {
  // The text is set in sketch-draw-interaction::finishTextDraw
  assert(style instanceof Style, 'The style of text features is a simple style');
  let text = style.getText().getText();
  if (Array.isArray(text)) {
    text = text.join('\n');
  }
  return text;
}

export async function imageStyle (properties: ImageSketchProperties) {
  const image = await loadImageFromProperties(properties);
  return getImageRendererStyle(image, properties);
}

export function getImageRendererStyle (image: HTMLImageElement | HTMLCanvasElement, { keepAspectRatio = true }) {
  const renderer = getImageObjectRenderer(image, keepAspectRatio);
  return new Style({ renderer });
}

export function loadImageFromProperties (properties: ImageSketchProperties) {
  const { contentIdentifier, repositoryName, signature } = properties;
  const imageSrc = getMediaItemDownloadUrl(contentIdentifier, repositoryName, signature);
  return loadImage(imageSrc);
}

function loadImage (src: string) {
  return new Promise<HTMLImageElement | HTMLCanvasElement>(resolve => {
    const icon = new Icon({ src });
    const image = icon.getImage(1);
    // Because of OL caching, the onload is not triggered if the image is already loaded
    if (icon.getImageState() === ImageState.LOADED) {
      resolve(image);
    } else {
      // Do not use image.onload, because for the same image src, due to caching, the second call
      // will overwrite the event listener, and the Promise would never get resolved
      on(image, 'load', () => resolve(image), { once: true });
      icon.load();
    }
  });
}

export function setupMeasureOverlay (feature: Feature, map: Map): Overlay {
  removeFeatureOverlay(feature, map);
  const overlay = createOverlay(map.getView().getProjection(), feature);
  map.addOverlay(overlay);
  feature.set(SketchFeatureProperty.OVERLAY_ID, overlay.getId());
  return overlay;
}

export function removeFeatureOverlay (feature: Feature, map: Map) {
  const currentFeatureOverlayId = feature.get(SketchFeatureProperty.OVERLAY_ID);
  if (currentFeatureOverlayId) {
    map.removeOverlay(map.getOverlayById(currentFeatureOverlayId));
  }
}

function createOverlay (projection: Projection, feature: Feature) {
  const geometry = feature.getGeometry();
  const properties = getSketchItemDto(feature)?.properties;
  assertType<MeasureSketchProperties | undefined>(properties, !properties
    || getSketchItemDto(feature)?.type === SketchItemType.AREA_MEASUREMENT
    || getSketchItemDto(feature)?.type === SketchItemType.DISTANCE_MEASUREMENT);
  const [ position, output ] = getPositionAndOutput(projection, geometry, properties?.unit !== 'imperial');
  return new Overlay({
    id: uniqueId(),
    element: h('.tooltip.tooltip--with-arrow.tooltip-arrow-below', output) as HTMLElement,
    offset: [ 0, -15 ],
    position,
    positioning: 'bottom-center'
  });
}

export function getPositionAndOutput (projection: Projection, geom?: Geometry, metric = true): [Coordinate | undefined, string] {
  let output = '';
  let anchorCoord;
  if (!geom) {
    return [ anchorCoord, output ];
  }
  if (geom instanceof Polygon) {
    const hasArea = geom.getCoordinates()[0].length > 3;
    output = formatArea(hasArea ? getArea(geom, { projection }) : 0, metric);
    anchorCoord = geom.getInteriorPoint().getCoordinates();
  } else if (geom instanceof LineString) {
    output = formatLength(getLength(geom, { projection }), false, false, metric);
    anchorCoord = geom.getLastCoordinate();
  } else {
    throw new Error(`Unsupported geometry of type: ${geom.getType()}`);
  }
  return [ anchorCoord, output ];
}

export function getDefaultStyleStrategy () {
  const primaryColor = toRgbaColor(window.Disy.theme['primary-color']);
  const borderColor = toRgbaColor(applyOpacityFactorToColor(primaryColor, 0.9));
  const fillColor = toRgbaColor(applyOpacityFactorToColor(primaryColor, 0.1));
  const defaultTextColor = 'rgba(0, 0, 0, 0.9)';
  const defaultTextBackground = 'rgba(255, 255, 255, 0.9)';
  const measureBorderColor = 'rgba(0, 0, 0, 0.9)';
  const lineWidth = 2;

  return createSketchTypeStrategy<SketchStyleProperties>({
    [SketchItemType.POINT]: () => getDefaultPointStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.OVAL]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.RECTANGLE]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.POLYGON]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.LINE_STRING]: () => getDefaultLineStringStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.DISTANCE_MEASUREMENT]: () => getDefaultMeasureDistanceStyle(fillColor, measureBorderColor, lineWidth),
    [SketchItemType.AREA_MEASUREMENT]: () => getDefaultMeasureAreaStyle(fillColor, measureBorderColor, lineWidth),
    [SketchItemType.COORDINATE]: () => getDefaultCoordinateStyle(),
    [SketchItemType.TEXT]: () => getDefaultTextStyle(defaultTextBackground, defaultTextColor),
    [SketchItemType.IMAGE]: () => {
      throw new Error('Unsupported. The image ol style is different, it is not created based on SketchStyleProperties.');
    },
    [SketchItemType.SYMBOL]: () => {
      throw new Error('Unsupported');
    }
  });
}

function getDefaultPointStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PointSketchStyle {
  return {
    [StyleProperty.POINT_SIGNATURE_TYPE]: PointSignatureType.CIRCLE,
    [StyleProperty.FOREGROUND_COLOR]: fillColor,
    [StyleProperty.SYMBOL_SIZE]: 10,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.BORDER_COLOR]: borderColor
  };
}

function getDefaultCoordinateStyle (): CoordinateSketchStyle {
  return {
    [StyleProperty.POINT_SIGNATURE_TYPE]: PointSignatureType.IMAGE,
    [StyleProperty.SYMBOL_SIZE]: 1,
    [StyleProperty.IMAGE_AS_BASE64]: 'data:image/svg+xml;base64,' + window.btoa(parseSvg(crosshair).outerHTML)
  };
}

function getDefaultLineStringStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): LineStringSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: borderColor,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.LINE_START_DECORATION]: LineDecoration.NONE,
    [StyleProperty.LINE_END_DECORATION]: LineDecoration.NONE
  };
}

function getDefaultMeasureDistanceStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): LineStringSketchStyle {
  return {
    ...getDefaultLineStringStyle(fillColor, borderColor, lineWidth),
    [StyleProperty.LINE_WIDTH]: 1,
    [StyleProperty.LINE_STYLE]: 'TinyDot'
  };
}

function getDefaultPolygonStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PolygonSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: fillColor,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.BORDER_COLOR]: borderColor
  };
}

function getDefaultMeasureAreaStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PolygonSketchStyle {
  return {
    ...getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [StyleProperty.LINE_WIDTH]: 1,
    [StyleProperty.LINE_STYLE]: 'TinyDot'
  };
}

function getDefaultTextStyle (fillColor: RgbaColor, borderColor: RgbaColor): TextSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: borderColor,
    [StyleProperty.BACKGROUND_COLOR]: fillColor,
    [StyleProperty.ANCHOR]: 'WEST',
    [StyleProperty.BOLD]: false,
    [StyleProperty.ITALIC]: false,
    [StyleProperty.FONT_SIZE]: 14,
    [StyleProperty.FONT_FAMILY]: fonts['Arial'],
    [StyleProperty.ROTATION]: 0
  };
}
